import { createApi } from '@reduxjs/toolkit/query/react'
import { Patient, PatientWithOptionalId } from '../types'
import { parse, isValid, format, endOfMonth } from 'date-fns'
import { axiosBaseQuery } from './baseQuery'
import { generatePaginationQuery } from '../utils/service/service-helper'

export type CreatePatientRequest = Omit<Patient, 'id'>

interface PatientsQueryObj {
  prescriberId?: string
  page?: number
  limit?: number
  searchTerm?: string
}

export const emptyPatient: PatientWithOptionalId = {
  given_names: '',
  family_name: '',
  gender: 'N',
  date_of_birth: '',
  ihi_number: '',
  ihi_status: '',
  record_status: '',
  entitlement_no: '',
  ctg_flag: false,
  hospital_category: false,
  pbs_entitlement_no: '',
  pbs_safety_net_entitlement_card_holder: false,
  pbs_concession_card_holder: false,
  email: '',
  phone_number: '',
  state: '',
  suburb: '',
  address_1: '',
  address_2: '',
  postcode: 0,
  medicare_no: '',
  medicare_irn: '',
  medicare_valid_to: '',
  dva_file_no: '',
  dva_card_colour: '',
  concession_pension_no: '',
  atsi_status: '',
  paper_only: false,
}

const transformPatientRequestPayload = <T>(data: Patient | CreatePatientRequest): T => {
  const { medicare_valid_to } = data
  if (medicare_valid_to && medicare_valid_to.match(/^\d{4}-\d{2}$/)) {
    const fullDate = medicare_valid_to + '-01'
    const parsedDate = parse(fullDate || '', 'yyyy-MM-dd', new Date())
    data.medicare_valid_to = isValid(parsedDate)
      ? format(endOfMonth(parsedDate), 'yyyy-MM-dd')
      : medicare_valid_to
  }

  return data as unknown as T
}

interface CreatePatientQueryObj {
  prescriberId?: string
  patient?: CreatePatientRequest
}

interface BatchCreatePatientsQueryObj {
  prescriberId?: string
  patients?: CreatePatientRequest[]
}

interface UpdatePatientQueryObj {
  prescriberId?: string
  patient: Patient
  patientId?: string
}

export type BatchCreatePatientsResponseItem = {
  data: {
    id: string
  } | null
  error: {
    reason: any
  } | null
}

// Define a service using a base URL and expected endpoints
export const patientsApi = createApi({
  reducerPath: 'patientsApi',
  tagTypes: ['Patients'],
  baseQuery: axiosBaseQuery({
    baseUrl: '/prescribers',
  }),
  endpoints: (build) => ({
    getPatientsByPrescriberId: build.query<
      { patients: Patient[]; meta: { totalPages: number; totalItems: number } },
      PatientsQueryObj
    >({
      query: ({ prescriberId, page, limit, searchTerm }) => {
        if (!prescriberId) {
          throw new Error('Prescriber Id is invalid')
        }

        const queryStr = generatePaginationQuery(limit, page, searchTerm)
        return {
          url: `/${prescriberId}/patients?${queryStr}`,
          method: 'get',
        }
      },
      // define which 'tag' is attached to the cached data returned by the query.
      providesTags: (result) =>
        result
          ? [
              ...result.patients.map(({ id }) => ({ type: 'Patients' as const, id })),
              { type: 'Patients', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Patients', id: 'PARTIAL-LIST' }],
    }),
    createPatient: build.mutation<Patient, CreatePatientQueryObj>({
      query: ({ patient, prescriberId }) => {
        if (!prescriberId) {
          throw new Error('Prescriber id is required to create patient.')
        }
        if (!patient) {
          throw new Error('New patient is required to create patient.')
        }
        const formattedPatient = transformPatientRequestPayload<CreatePatientRequest>(patient)
        return {
          url: `/${prescriberId}/patients`,
          method: 'post',
          data: formattedPatient,
        }
      },
      // Determines which cached data should be either re-fetched or removed from the cache. Expects the same shapes as providesTags.
      invalidatesTags: ['Patients'],
    }),
    batchCreatePatients: build.mutation<
      BatchCreatePatientsResponseItem[],
      BatchCreatePatientsQueryObj
    >({
      query: ({ patients, prescriberId }) => {
        if (!prescriberId) {
          throw new Error('Prescriber id is required to create patient.')
        }
        if (!patients?.length) {
          throw new Error('Patients data are required to batch create patients.')
        }
        const formattedPatients = patients.map((patient) => {
          const fullPatientData = { ...emptyPatient, ...patient }
          return transformPatientRequestPayload<CreatePatientRequest>(fullPatientData)
        })
        return {
          url: `/${prescriberId}/patients/batch`,
          method: 'post',
          data: formattedPatients,
        }
      },
      // Determines which cached data should be either re-fetched or removed from the cache. Expects the same shapes as providesTags.
      invalidatesTags: ['Patients'],
    }),
    updatePatient: build.mutation<Patient, UpdatePatientQueryObj>({
      query: ({ patient, prescriberId, patientId }) => {
        if (!prescriberId || !patientId) {
          throw new Error('Prescriber id and patient id are required to update patient.')
        }

        if (!patient) {
          throw new Error('New patient is required to update patient.')
        }

        const formattedPatient = transformPatientRequestPayload<Patient>(patient)
        return {
          url: `/${prescriberId}/patients/${patientId}`,
          method: 'patch',
          data: formattedPatient,
        }
      },
      invalidatesTags: (result, error, { patientId }) => [{ type: 'Patients', id: patientId }],
    }),
    deletePatient: build.mutation<null, { patientId?: string; prescriberId?: string }>({
      query: ({ prescriberId, patientId }) => {
        if (!prescriberId || !patientId) {
          throw new Error('Prescriber id and patient id are required to update patient.')
        }

        return {
          url: `/${prescriberId}/patients/${patientId}`,
          method: 'delete',
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: 'Patients', id: patientId },
        { type: 'Patients', id: 'PARTIAL-LIST' },
      ],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPatientsByPrescriberIdQuery,
  useUpdatePatientMutation,
  useDeletePatientMutation,
  useCreatePatientMutation,
  useBatchCreatePatientsMutation,
  useLazyGetPatientsByPrescriberIdQuery,
} = patientsApi
